import { useEffect, useState, useCallback } from "react";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import { ArrowsContainer, ArrowBtn, ButtonsWrap } from "./localStyle";

/*
  Componente: ScrollArrows
  Descripción: Muestra dos botones (arriba/abajo) para desplazar suavemente
  el contenedor objetivo. Útil en tablet/desktop cuando la barra de scroll está oculta.
  También habilita drag-to-scroll (táctil y mouse) en el contenedor.
*/
const ScrollArrows = ({ targetSelector, stepPx = 0 }) => {
  const [canUp, setCanUp] = useState(false);
  const [canDown, setCanDown] = useState(false);
  const [targetEl, setTargetEl] = useState(null);

  useEffect(() => {
    let intervalId = null;

    const setup = (element) => {
      setTargetEl(element);

      const updateState = () => {
        const { scrollTop, clientHeight, scrollHeight } = element;
        setCanUp(scrollTop > 0);
        setCanDown(scrollTop + clientHeight < scrollHeight - 1);
      };

      updateState();
      element.addEventListener("scroll", updateState);
      window.addEventListener("resize", updateState);

      // Detectar cambios en el contenido del contenedor (carga async de cards)
      const mo = new MutationObserver(updateState);
      mo.observe(element, { childList: true, subtree: true });

      // --- Drag-to-scroll (táctil / mouse) ---
      let isDragging = false;
      let startY = 0;
      let startScroll = 0;

      const getY = (e) => (e.touches ? e.touches[0].clientY : e.clientY);

      const onPointerDown = (e) => {
        if (e.target.closest("button, a")) return;
        isDragging = true;
        startY = getY(e);
        startScroll = element.scrollTop;
        element.style.cursor = "grabbing";
        element.style.userSelect = "none";
      };

      const onPointerMove = (e) => {
        if (!isDragging) return;
        const delta = startY - getY(e);
        element.scrollTop = startScroll + delta;
      };

      const onPointerUp = () => {
        if (!isDragging) return;
        isDragging = false;
        element.style.cursor = "";
        element.style.userSelect = "";
      };

      element.addEventListener("mousedown", onPointerDown);
      element.addEventListener("touchstart", onPointerDown, { passive: true });
      window.addEventListener("mousemove", onPointerMove);
      window.addEventListener("touchmove", onPointerMove, { passive: true });
      window.addEventListener("mouseup", onPointerUp);
      window.addEventListener("touchend", onPointerUp);

      return () => {
        element.removeEventListener("scroll", updateState);
        window.removeEventListener("resize", updateState);
        mo.disconnect();
        element.removeEventListener("mousedown", onPointerDown);
        element.removeEventListener("touchstart", onPointerDown);
        window.removeEventListener("mousemove", onPointerMove);
        window.removeEventListener("touchmove", onPointerMove);
        window.removeEventListener("mouseup", onPointerUp);
        window.removeEventListener("touchend", onPointerUp);
      };
    };

    let cleanup = null;
    const el = document.querySelector(targetSelector);

    if (el) {
      cleanup = setup(el);
    } else {
      intervalId = setInterval(() => {
        const found = document.querySelector(targetSelector);
        if (found) {
          clearInterval(intervalId);
          intervalId = null;
          cleanup = setup(found);
        }
      }, 200);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (cleanup) cleanup();
    };
  }, [targetSelector]);

  const getStep = useCallback(() => {
    if (!targetEl) return 0;
    if (stepPx > 0) return stepPx;
    return Math.floor(targetEl.clientHeight * 0.85);
  }, [targetEl, stepPx]);

  const scrollByAmount = useCallback(
    (amount) => {
      if (!targetEl) return;
      targetEl.scrollBy({ top: amount, behavior: "smooth" });
    },
    [targetEl]
  );

  return (
    <ArrowsContainer>
      <ButtonsWrap>
        <ArrowBtn
          type="button"
          aria-label="Desplazar hacia arriba"
          disabled={!canUp}
          onClick={() => scrollByAmount(-getStep())}
        >
          <FaChevronUp />
        </ArrowBtn>
        <ArrowBtn
          type="button"
          aria-label="Desplazar hacia abajo"
          disabled={!canDown}
          onClick={() => scrollByAmount(getStep())}
        >
          <FaChevronDown />
        </ArrowBtn>
      </ButtonsWrap>
    </ArrowsContainer>
  );
};

export default ScrollArrows;
